/**
 * Departments — sv_organization. The company is addressed two DIFFERENT ways here, and
 * telling them apart is the difference between a call and a 403:
 *
 *   GET    /companies/{id}/departments   → {meta, data}   company in the PATH
 *   POST   /companies/{id}/departments   → Department     company in the PATH
 *   GET    /departments/{id}             → Department     company NOWHERE — header
 *   PUT    /departments/{id}             → Department     company NOWHERE — header
 *   DELETE /departments/{id}             → {}             company NOWHERE — header
 *
 * The gateway picks the Casbin DOMAIN it matches the caller's own policy in from
 * `X-Company-Id`, and falls back to `system` when the header is absent. An owner's
 * policy is written as owner / company:{id} — in `system` they hold nothing. The
 * `/departments/{id}` routes name the company neither in the path nor in the body, so
 * without the header even the company's owner is an outsider and gets
 * PERMISSION_DENIED on their own department. Hence requestInCompany() on those three —
 * the same wire as requestControl(), under the name that reads right outside
 * `/control/*`. Until 2026-09-04 all five went through request(), on the strength of a
 * rule ("company is in the path") that is only true of the first two.
 *
 * The header widens nothing on the WRITES: sv_organization resolves the department's
 * OWN company and checks the caller against it (`manage_departments.rs` — `delete`
 * answers "Only company owner can delete departments", `PUT` goes through
 * `verify_access`), so a header naming somebody else's company changes no answer there.
 * It opens the door only to a caller who already holds the key. The READ is the
 * exception, and it is spelled out on {@link fetchDepartment} below.
 */
import {request, requestInCompany} from '@/shared/api';
import type {
  Department,
  DepartmentInput,
  PagedDepartments,
} from './types';

export type FetchDepartmentsParams = {
  search?: string;
  page?: number;
  perPage?: number;
  /** Exact and case-insensitive, unlike `search`. */
  departmentType?: string;
  /** Direct children of one department — one level, not the whole subtree. */
  parentId?: string;
  isActive?: boolean;
  /** `name` (default) | `department_type` | `code`. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

/**
 * The company's departments.
 *
 * Every filter, the sort and the paging run in the GATEWAY, over the whole of the
 * company's departments fetched in one unpaginated call — so `meta.total` is the size
 * of the FILTERED set, counted after filtering and before paging. It is not the
 * company's department count, and a screen that prints it as one will be wrong the
 * moment anybody types in the search box.
 */
export function fetchDepartments(
  companyId: string,
  params: FetchDepartmentsParams = {},
): Promise<PagedDepartments> {
  const qs = new URLSearchParams();
  if (params.search) {
    qs.set('search', params.search);
  }
  if (params.page !== undefined) {
    qs.set('page', String(params.page));
  }
  if (params.perPage !== undefined) {
    qs.set('per_page', String(params.perPage));
  }
  if (params.departmentType) {
    qs.set('department_type', params.departmentType);
  }
  if (params.parentId) {
    qs.set('parent_id', params.parentId);
  }
  if (params.isActive !== undefined) {
    qs.set('is_active', String(params.isActive));
  }
  if (params.sortBy) {
    qs.set('sort_by', params.sortBy);
  }
  if (params.sortOrder) {
    qs.set('sort_order', params.sortOrder);
  }
  const query = qs.toString();
  return request<PagedDepartments>(
    query
      ? `/companies/${encodeURIComponent(companyId)}/departments?${query}`
      : `/companies/${encodeURIComponent(companyId)}/departments`,
  );
}

export function createDepartment(
  companyId: string,
  input: DepartmentInput,
): Promise<Department> {
  return request<Department>(
    `/companies/${encodeURIComponent(companyId)}/departments`,
    {method: 'POST', body: JSON.stringify(input)},
  );
}

/**
 * One department by id.
 *
 * ⚠️ This route does NOT check that `{id}` belongs to the company whose header
 * authorized the call — Casbin's domain is built from the header alone, and
 * `get_department` fetches by id and returns whatever sv_organization has. Passing an
 * id that came from anywhere but this company's own list is therefore a read across a
 * tenant boundary the gateway will not stop. Feed it ids from
 * {@link fetchDepartments}, never from a URL or a paste.
 */
export function fetchDepartment(
  companyId: string,
  deptId: string,
): Promise<Department> {
  return requestInCompany<Department>(
    `/departments/${encodeURIComponent(deptId)}`,
    companyId,
  );
}

export function updateDepartment(
  companyId: string,
  deptId: string,
  input: DepartmentInput,
): Promise<Department> {
  return requestInCompany<Department>(
    `/departments/${encodeURIComponent(deptId)}`,
    companyId,
    {method: 'PUT', body: JSON.stringify(input)},
  );
}

export function deleteDepartment(
  companyId: string,
  deptId: string,
): Promise<void> {
  return requestInCompany<void>(
    `/departments/${encodeURIComponent(deptId)}`,
    companyId,
    {method: 'DELETE'},
  );
}
